import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@bem-react/classname'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Memo } from '@/hoc/Memo'
import yup from '@/utils/yup-extended'
import useBackendError from '@/hooks/useBackendError'
import { useAppDispatch } from '@/core/store/hooks'
import { ChangePasswordModel } from '../../models/UserModel'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { changePasswordThunk } from '@/core/services/Auth/store/auth.slice'

import { Field } from '@/components/form/Field'
import { Button } from '@/components/Button'

import './ChangePasswordForm.scss'

const cnChangePasswordForm = cn('ChangePasswordForm')

export const ChangePasswordForm: FC = Memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [setBackendError, fieldsError] = useBackendError()

  const ChangePasswordSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .trim()
      .required(t('Old Password is a required field')),
    newPassword: yup
      .string()
      .trim()
      .required(t('Password is a required field'))
      .min(8, t('Password must be at least 8 characters'))
      .isValidPassword(t('isInvalidPassword')),
    confirmPassword: yup
      .string()
      .trim()
      .required(t('Confirm Password is a required field'))
      .min(8, t('Password must be at least 8 characters'))
      .isValidPassword(t('isInvalidPassword'))
      .oneOf([yup.ref('newPassword'), null], t('Passwords must match')),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordModel>({
    mode: 'onChange',
    resolver: yupResolver(ChangePasswordSchema),
  })

  const changePassword = useCallback(async (data: ChangePasswordModel) => {
    try {
      const res = await dispatch(changePasswordThunk(data)).unwrap()
      if (res === 200) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t('Password successfully updated'),
          }),
        )
        reset(new ChangePasswordModel())
      }
    } catch (errors) {
      setBackendError(errors)
    }
  }, [])

  return (
    <form
      className={cnChangePasswordForm('form')}
      onSubmit={handleSubmit(changePassword)}
    >
      <div className={cnChangePasswordForm('grid')}>
        <Field
          {...register('oldPassword')}
          type='password'
          placeholder={t('Old Password')}
          label={t('Old Password')}
          required
          errors={
            (errors.oldPassword && errors.oldPassword.message) ||
            (fieldsError &&
              fieldsError.oldPassword &&
              fieldsError.oldPassword.message)
          }
        />

        <Field
          {...register('newPassword')}
          type='password'
          placeholder={t('New Password')}
          label={t('New Password')}
          required
          errors={
            (errors.newPassword && errors.newPassword.message) ||
            (fieldsError &&
              fieldsError.newPassword &&
              fieldsError.newPassword.message)
          }
        />
        <Field
          {...register('confirmPassword')}
          type='password'
          placeholder={t('Confirm Password')}
          label={t('Confirm Password')}
          required
          errors={
            (errors.confirmPassword && errors.confirmPassword.message) ||
            (fieldsError &&
              fieldsError.confirmPassword &&
              fieldsError.confirmPassword.message)
          }
        />
      </div>

      <div className={cnChangePasswordForm('btn')}>
        <Button
          className={cnChangePasswordForm('button')}
          type='submit'
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        >
          {t('Submit')}
        </Button>
      </div>
    </form>
  )
})
